import { useEffect, useState } from 'react';
import UnbanModal from '../../forms/UnbanModal';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

interface BanRow {
    _id: string;
    idHash: string;
    status: 'active' | 'inactive';
    reason: string;
    notes: string;
    bannedAt: string;
    bannedBy: string;
    last4: string;
    issuingStateOrCountry: string;
    deactivatedAt: string | null;
    deactivatedBy: string;
    deactivationReason: string;
    history: Array<{
        action: 'BAN' | 'UNBAN';
        actor: string;
        reason: string;
        at: string;
    }>;
    createdAt?: string;
    updatedAt?: string;
}

const StatusPill = ({ status }: { status: BanRow['status'] }) => (
    <span
        style={{
            padding: '2px 10px',
            borderRadius: 99,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: 1,
            background: status === 'active' ? '#3a1a1a' : '#222',
            color: status === 'active' ? '#ff5555' : '#888',
            border:
                '1px solid ' + (status === 'active' ? '#ff5555' : '#555'),
        }}
    >
        {status.toUpperCase()}
    </span>
);

const BansListPage = () => {
    const history = useHistory();
    const [rows, setRows] = useState<BanRow[]>([]);
    const [filter, setFilter] = useState<'active' | 'inactive' | ''>('active');
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [liftTarget, setLiftTarget] = useState<BanRow | null>(null);
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const r = await axios.get('/api/bans', {
                params: filter ? { status: filter } : {},
            });
            setRows(r.data || []);
        } catch (e: any) {
            setError(e?.response?.data?.message || e.message || 'Load failed');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const lift = (row: BanRow) => {
        // Open the UnbanModal — reason is enforced both client-side
        // (UnbanModal disables Confirm until non-blank) and
        // server-side (BansService.deactivate throws BadRequest on
        // empty reason).
        setLiftTarget(row);
    };

    const submitLift = async (reason: string) => {
        if (!liftTarget) return;
        try {
            await axios.patch(`/api/bans/${liftTarget._id}/deactivate`, {
                reason,
            });
            setLiftTarget(null);
            await load();
        } catch (e: any) {
            alert(
                'Lift failed: ' +
                    (e?.response?.data?.message || e.message),
            );
        }
    };

    return (
        <div style={{ padding: 24, color: '#fff' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 18,
                }}
            >
                <h1 style={{ margin: 0 }}>🚫 Banned Guests</h1>
                <div style={{ display: 'flex', gap: 8 }}>
                    <select
                        value={filter}
                        onChange={(e) =>
                            setFilter(e.target.value as 'active' | 'inactive' | '')
                        }
                        style={{
                            background: '#111',
                            color: '#fff',
                            border: '1px solid #444',
                            borderRadius: 6,
                            padding: '6px 10px',
                        }}
                    >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="">All</option>
                    </select>
                    <button
                        onClick={() => history.push('/admin/bans/from-scan')}
                        style={{
                            background: '#3a1a1a',
                            color: '#fff',
                            border: '1px solid #ff5555',
                            borderRadius: 6,
                            padding: '6px 14px',
                            cursor: 'pointer',
                            fontWeight: 700,
                        }}
                    >
                        + Ban from scan
                    </button>
                    <button
                        onClick={() => history.push('/dashboard')}
                        style={{
                            background: '#222',
                            color: '#aaa',
                            border: '1px solid #444',
                            borderRadius: 6,
                            padding: '6px 14px',
                            cursor: 'pointer',
                        }}
                    >
                        ← Back
                    </button>
                </div>
            </div>

            {error && (
                <div
                    style={{
                        background: '#3a1a1a',
                        color: '#ff8888',
                        padding: 10,
                        borderRadius: 6,
                        marginBottom: 16,
                    }}
                >
                    {error}
                </div>
            )}

            {loading ? (
                <div>Loading…</div>
            ) : rows.length === 0 ? (
                <div style={{ color: '#888' }}>No bans match this filter.</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #333' }}>
                            <th style={th}>Status</th>
                            <th style={th}>Banned at</th>
                            <th style={th}>Last 4</th>
                            <th style={th}>State</th>
                            <th style={th}>Reason</th>
                            <th style={th}>Banned by</th>
                            <th style={th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <>
                                <tr
                                    key={r._id}
                                    style={{ borderBottom: '1px solid #222' }}
                                >
                                    <td style={td}>
                                        <StatusPill status={r.status} />
                                    </td>
                                    <td style={td}>
                                        {r.bannedAt
                                            ? new Date(r.bannedAt).toLocaleString()
                                            : '—'}
                                    </td>
                                    <td style={{ ...td, fontFamily: 'monospace' }}>
                                        {r.last4 || '----'}
                                    </td>
                                    <td style={td}>
                                        {r.issuingStateOrCountry || '—'}
                                    </td>
                                    <td style={td}>{r.reason}</td>
                                    <td
                                        style={{
                                            ...td,
                                            fontFamily: 'monospace',
                                            fontSize: '0.8rem',
                                        }}
                                    >
                                        {r.bannedBy
                                            ? r.bannedBy.slice(-8)
                                            : '—'}
                                    </td>
                                    <td style={td}>
                                        <button
                                            onClick={() =>
                                                setExpanded(
                                                    expanded === r._id
                                                        ? null
                                                        : r._id,
                                                )
                                            }
                                            style={btnSm}
                                        >
                                            {expanded === r._id ? '▴' : '▾'}
                                        </button>{' '}
                                        {r.status === 'active' && (
                                            <button
                                                onClick={() => lift(r)}
                                                style={{
                                                    ...btnSm,
                                                    background: '#1a3a1a',
                                                    border: '1px solid #39ff14',
                                                    color: '#9be39b',
                                                }}
                                            >
                                                Lift
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                {expanded === r._id && (
                                    <tr style={{ background: '#0a0a14' }}>
                                        <td
                                            colSpan={7}
                                            style={{ padding: '10px 16px' }}
                                        >
                                            {r.notes && (
                                                <div
                                                    style={{
                                                        marginBottom: 8,
                                                        fontStyle: 'italic',
                                                        color: '#ccc',
                                                    }}
                                                >
                                                    📝 {r.notes}
                                                </div>
                                            )}
                                            <div
                                                style={{
                                                    fontSize: '0.78rem',
                                                    color: '#888',
                                                    marginBottom: 6,
                                                }}
                                            >
                                                History:
                                            </div>
                                            {(r.history || []).map((h, i) => (
                                                <div
                                                    key={i}
                                                    style={{
                                                        fontSize: '0.78rem',
                                                        fontFamily: 'monospace',
                                                        marginLeft: 8,
                                                    }}
                                                >
                                                    {new Date(h.at).toLocaleString()} ·{' '}
                                                    <span
                                                        style={{
                                                            color:
                                                                h.action === 'BAN'
                                                                    ? '#ff8888'
                                                                    : '#88ff88',
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {h.action}
                                                    </span>{' '}
                                                    by {(h.actor || '—').slice(-8)}{' '}
                                                    {h.reason ? `· ${h.reason}` : ''}
                                                </div>
                                            ))}
                                        </td>
                                    </tr>
                                )}
                            </>
                        ))}
                    </tbody>
                </table>
            )}

            {liftTarget && (
                <UnbanModal
                    targetLabel={
                        `last4=${liftTarget.last4 || '----'}` +
                        (liftTarget.issuingStateOrCountry
                            ? ` ${liftTarget.issuingStateOrCountry}`
                            : '') +
                        (liftTarget.reason
                            ? ` · banned for: ${liftTarget.reason}`
                            : '')
                    }
                    onCancel={() => setLiftTarget(null)}
                    onSubmit={submitLift}
                />
            )}
        </div>
    );
};

const th: React.CSSProperties = {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: '0.8rem',
    color: '#888',
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
};
const td: React.CSSProperties = {
    padding: '10px',
    fontSize: '0.92rem',
    verticalAlign: 'middle',
};
const btnSm: React.CSSProperties = {
    background: '#222',
    color: '#ccc',
    border: '1px solid #444',
    borderRadius: 4,
    padding: '4px 10px',
    cursor: 'pointer',
    fontSize: '0.85rem',
};

export default BansListPage;
